import { ArrowDown, ArrowUp, Bot, Check, Plus, ShieldAlert, Trash2, Wand2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useConfirm } from "@/components/ConfirmDialog";
import { QUALITY_LABEL, scoreSelector } from "@/lib/selectorQuality";
import { api, formatApiError } from "@/lib/api";

const ACTIONS = [
  { value: "goto", label: "Navigate (goto)" },
  { value: "click", label: "Click" },
  { value: "fill", label: "Fill input" },
  { value: "press", label: "Press key" },
  { value: "wait", label: "Wait (ms)" },
  { value: "assert_text", label: "Assert text" },
  { value: "assert_visible", label: "Assert visible" },
];

const NEEDS_SELECTOR = ["click", "fill", "assert_visible", "assert_text"];
const VALUE_PLACEHOLDER = {
  fill: "Text to type",
  press: "Key, e.g. Enter",
  wait: "Milliseconds, e.g. 1500",
  assert_text: "Expected text",
};
// Actions that honour a per-step Timeout override. `goto` uses a fixed 30s
// navigation timeout; `wait` is a sleep so the value already IS the duration.
const SUPPORTS_TIMEOUT = ["click", "fill", "press", "assert_text", "assert_visible"];

export const StepEditor = ({ steps, onChange, testId, onMutated }) => {
  const confirm = useConfirm();
  const [busyShadowStep, setBusyShadowStep] = useState(null);
  const update = (i, patch) => {
    const next = steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    onChange(next);
  };
  const remove = async (i) => {
    const ok = await confirm({
      title: `Delete step ${i + 1}?`,
      description: "This step will be removed.",
      confirmLabel: "Delete",
      danger: true,
      testId: "confirm-delete-step",
    });
    if (!ok) return;
    onChange(steps.filter((_, idx) => idx !== i));
  };
  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    // Confirm before reordering — accidental clicks on the ↑ / ↓
    // buttons were a recurring source of "why did the test break?"
    // confusion for the QA team. A 1-click confirmation is cheap and
    // matches the pattern already used for delete.
    const direction = dir === -1 ? "up" : "down";
    const ok = await confirm({
      title: `Move step ${i + 1} ${direction}?`,
      description: `Step ${i + 1} will swap places with step ${j + 1}. Reordering can change how the test runs.`,
      confirmLabel: dir === -1 ? "Move up" : "Move down",
      danger: false,
      testId: "confirm-move-step",
    });
    if (!ok) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const addStep = () =>
    onChange([...steps, { id: crypto.randomUUID(), action: "click", selector: "", value: "", url: "", description: "" }]);

  return (
    <TooltipProvider delayDuration={120} skipDelayDuration={0}>
    <div className="space-y-0" data-testid="step-editor">
      {steps.length === 0 && (
        <div className="surface-card border-dashed p-8 text-center text-sm text-[#64748B]" data-testid="empty-steps-message">
          No steps yet. Add a step below or re-create this test with the recorder.
        </div>
      )}
      <div className={steps.length > 0 ? "surface-card divide-y divide-[#F1F5F9] overflow-hidden" : ""}>
        {/* Column header — appears once above the rows. Hidden below
            the `sm` breakpoint where the grid collapses to a stacked
            mobile layout (column headers would lose alignment). */}
        {steps.length > 0 && (
          <div
            className="hidden sm:grid grid-cols-[32px_160px_1fr_1fr_120px_auto] gap-2.5 items-center bg-[#F8FAFC] border-b border-[#E5E7EB] px-4 py-2 text-[11px] font-semibold text-[#64748B] uppercase tracking-wider"
            data-testid="step-editor-header-row"
          >
            <span>#</span>
            <span>Action</span>
            <span>Selector</span>
            <span>Value</span>
            <span>Timeout (s)</span>
            <span className="text-right pr-1">Actions</span>
          </div>
        )}
        {steps.map((step, i) => (
          <div
            key={step.id || i}
            data-testid={`step-row-${i}`}
            className="grid grid-cols-[28px_1fr_auto] sm:grid-cols-[32px_160px_1fr_1fr_120px_auto] gap-2.5 items-start bg-white px-3 sm:px-4 py-3"
          >
            <span className="font-mono text-[11px] text-[#94A3B8] row-span-3 sm:row-span-1">{String(i + 1).padStart(2, "0")}</span>
            <Select value={step.action} onValueChange={(v) => update(i, { action: v })}>
              <SelectTrigger className="rounded-md border-[#E5E7EB] h-9 text-sm col-span-1 focus:ring-[#3B82F6]" data-testid={`step-action-select-${i}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-md border-[#E5E7EB]">
                {ACTIONS.map((a) => (
                  <SelectItem key={a.value} value={a.value} className="text-sm">
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {step.action === "goto" ? (
              <Input
                data-testid={`step-url-input-${i}`}
                value={step.url || ""}
                onChange={(e) => update(i, { url: e.target.value })}
                placeholder="https://..."
                className="rounded-md border-[#E5E7EB] h-9 font-mono text-xs col-start-2 sm:col-start-3 sm:col-span-3 focus-visible:ring-[#3B82F6] focus-visible:border-[#3B82F6]"
              />
            ) : (
              <>
                <div className="col-start-2 sm:col-start-3 space-y-1">
                  <Input
                    data-testid={`step-selector-input-${i}`}
                    value={step.selector || ""}
                    onChange={(e) => update(i, { selector: e.target.value })}
                    placeholder={NEEDS_SELECTOR.includes(step.action) ? "CSS selector" : "—"}
                    disabled={!NEEDS_SELECTOR.includes(step.action)}
                    className="rounded-md border-[#E5E7EB] h-9 font-mono text-xs disabled:bg-[#F8FAFC] focus-visible:ring-[#3B82F6] focus-visible:border-[#3B82F6]"
                  />
                  <SelectorMeta
                    step={step}
                    index={i}
                    testId={testId}
                    busy={busyShadowStep === step.id}
                    setBusy={(v) => setBusyShadowStep(v ? step.id : null)}
                    onMutated={onMutated}
                  />
                </div>
                <Input
                  data-testid={`step-value-input-${i}`}
                  value={step.value || ""}
                  onChange={(e) => update(i, { value: e.target.value })}
                  placeholder={VALUE_PLACEHOLDER[step.action] || "—"}
                  disabled={!VALUE_PLACEHOLDER[step.action]}
                  className="rounded-md border-[#E5E7EB] h-9 font-mono text-xs disabled:bg-[#F8FAFC] col-start-2 sm:col-start-4 focus-visible:ring-[#3B82F6] focus-visible:border-[#3B82F6]"
                />
                <TimeoutInputSeconds
                  index={i}
                  step={step}
                  supportsTimeout={SUPPORTS_TIMEOUT.includes(step.action)}
                  onPatch={(patch) => update(i, patch)}
                />
              </>
            )}
            <div className="flex items-center gap-1 row-start-1 col-start-3 sm:col-start-6">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => move(i, -1)}
                    data-testid={`step-move-up-${i}`}
                    aria-label="Move step up"
                    className="flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8FAFC] disabled:opacity-30"
                    disabled={i === 0}
                  >
                    <ArrowUp size={13} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-[#0F172A] text-white">Move step up</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => move(i, 1)}
                    data-testid={`step-move-down-${i}`}
                    aria-label="Move step down"
                    className="flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8FAFC] disabled:opacity-30"
                    disabled={i === steps.length - 1}
                  >
                    <ArrowDown size={13} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-[#0F172A] text-white">Move step down</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => remove(i)}
                    data-testid={`step-delete-${i}`}
                    aria-label="Delete step"
                    className="flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] text-[#B91C1C] hover:bg-[#FEF2F2] hover:border-[#FCA5A5]"
                  >
                    <Trash2 size={13} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-[#B91C1C] text-white">Delete step</TooltipContent>
              </Tooltip>
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={addStep}
        data-testid="add-step-button"
        className="mt-3 inline-flex items-center gap-1.5 px-4 h-9 text-sm font-medium rounded-md border border-[#E5E7EB] bg-white text-[#0F172A] hover:bg-[#F8FAFC] hover:border-[#CBD5E1] transition-colors"
      >
        <Plus size={13} />
        Add step
      </button>
    </div>
    </TooltipProvider>
  );
};


/**
 * Timeout input — display + edit in **seconds** (storage stays ms).
 *
 *   • The team was getting confused by raw millisecond numbers (e.g.
 *     `18250`). Seconds (`18.3`) is the human-scale unit everyone
 *     reasons about.
 *   • Local string state so the user can type fractional values like
 *     `6.` mid-edit without the controlled-input snapping them shut.
 *   • Re-syncs from `step.timeout_ms` whenever it changes externally
 *     (step reorder, parent save reload, auto-clamp).
 *   • On blur, clamps the parsed value to [1, 300] seconds and writes
 *     back to `step.timeout_ms` in ms. Empty stays empty so the
 *     placeholder (`auto ~18s`) keeps showing the inherited default.
 */
function TimeoutInputSeconds({ index, step, supportsTimeout, onPatch }) {
  const msToSecStr = (ms) =>
    ms === null || ms === undefined ? "" : String(Number((ms / 1000).toFixed(2)));
  const [text, setText] = useState(() => msToSecStr(step.timeout_ms));
  useEffect(() => {
    setText(msToSecStr(step.timeout_ms));
  }, [step.timeout_ms]);

  const autoMs = step.recorded_duration_ms
    ? Math.max(5000, Math.min(step.recorded_duration_ms * 2 + 3000, 60000))
    : null;
  const placeholderSec = autoMs ? `auto ~${Math.round(autoMs / 1000)}s` : "10s";
  const tooltip = step.recorded_duration_ms
    ? `Per-step timeout (seconds). Auto-derived as 2× recorded latency (${(step.recorded_duration_ms / 1000).toFixed(1)}s) + 3s, clamped 5–60s. Override here. Range 1–300 seconds.`
    : "Per-step timeout (seconds). Default 10s. Range 1–300 seconds.";

  return (
    <Input
      data-testid={`step-timeout-input-${index}`}
      type="text"
      inputMode="decimal"
      value={text}
      onChange={(e) => {
        // Allow digits and ONE decimal point. Strip everything else
        // so the field stays safe to `Number()` even mid-edit.
        const raw = e.target.value.replace(/[^\d.]/g, "");
        const parts = raw.split(".");
        const clean =
          parts.length > 1
            ? parts[0] + "." + parts.slice(1).join("").slice(0, 2)
            : raw;
        setText(clean);
        if (clean === "" || clean === ".") {
          if (step.timeout_ms != null) onPatch({ timeout_ms: null });
          return;
        }
        const n = Number(clean);
        if (Number.isFinite(n)) {
          const ms = Math.round(n * 1000);
          if (ms !== step.timeout_ms) onPatch({ timeout_ms: ms });
        }
      }}
      onBlur={() => {
        if (text === "" || text === ".") {
          setText("");
          if (step.timeout_ms != null) onPatch({ timeout_ms: null });
          return;
        }
        const n = Number(text);
        if (!Number.isFinite(n)) {
          setText(msToSecStr(step.timeout_ms));
          return;
        }
        // Clamp to 1–300 seconds. Store as ms.
        const clampedSec = Math.max(1, Math.min(n, 300));
        const ms = Math.round(clampedSec * 1000);
        setText(String(Number(clampedSec.toFixed(2))));
        if (ms !== step.timeout_ms) onPatch({ timeout_ms: ms });
      }}
      placeholder={placeholderSec}
      disabled={!supportsTimeout}
      title={tooltip}
      className="rounded-md border-[#E5E7EB] h-9 font-mono text-xs disabled:bg-[#F8FAFC] col-start-2 sm:col-start-5 focus-visible:ring-[#3B82F6] focus-visible:border-[#3B82F6]"
    />
  );
}


/**
 * Per-step selector annotations:
 *   • Quality pill (Excellent / Good / Fragile / Broken) inferred from
 *     the current `selector` string.
 *   • Shadow pill — when the runner has captured a pending healed
 *     selector (after N consecutive successful runs it auto-promotes;
 *     in the meantime owners can manually Accept or Dismiss).
 *   • Heal-event count badge — clickable in the future to expand the
 *     audit trail; for now an at-a-glance signal that the step has
 *     been healed at least once.
 */
function SelectorMeta({ step, index, testId, busy, setBusy, onMutated }) {
  const selector = step.selector || "";
  const recoveryHints = (step.recovery_hints || []).filter((h) => h && h.fingerprint);
  if (!selector && !step.shadow_selector && recoveryHints.length === 0) return null;
  const quality = scoreSelector(selector);
  const tint = QUALITY_LABEL[quality.quality] || QUALITY_LABEL.fragile;
  const shadow = step.shadow_selector;
  const shadowCount = step.shadow_success_count || 0;
  const healCount = (step.heal_events || []).length;

  const acceptShadow = async () => {
    if (!testId || !step.id) return;
    setBusy(true);
    try {
      await api.post(`/tests/${testId}/steps/${step.id}/accept-heal`);
      toast.success("Healed selector promoted to primary");
      onMutated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const dismissShadow = async () => {
    if (!testId || !step.id) return;
    setBusy(true);
    try {
      await api.post(`/tests/${testId}/steps/${step.id}/dismiss-heal`);
      toast.success("Pending heal dismissed");
      onMutated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const discardRecoveryHint = async (fingerprint) => {
    if (!testId || !step.id) return;
    setBusy(true);
    try {
      await api.delete(`/tests/${testId}/steps/${step.id}/recovery-hints/${fingerprint}`);
      toast.success("Learned recovery discarded");
      onMutated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap pl-0.5" data-testid={`step-meta-${index}`}>
      {selector && (
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold"
          style={{ backgroundColor: tint.bg, color: tint.tint }}
          title={quality.reason}
        >
          {quality.quality === "fragile" || quality.quality === "broken" ? <ShieldAlert size={9} /> : null}
          {tint.label}
        </span>
      )}
      {healCount > 0 && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#10B981]/10 text-[#047857]" title={`${healCount} heal events`}>
          <Wand2 size={9} /> {healCount}
        </span>
      )}
      {shadow && (
        <div
          className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-[#3B82F6]/30 bg-[#3B82F6]/5 text-[10px]"
          data-testid={`step-shadow-${index}`}
          title={`Healed selector — auto-promotes after 3 successful runs (currently ${shadowCount}/3)`}
        >
          <span className="font-medium text-[#1D4ED8]">🩹 Pending heal</span>
          <span className="font-mono text-[#475569] max-w-[180px] truncate">{shadow}</span>
          <span className="text-[#94A3B8]">{shadowCount}/3</span>
          {testId && (
            <>
              <button
                onClick={acceptShadow}
                disabled={busy}
                data-testid={`step-shadow-accept-${index}`}
                title="Accept this healed selector now"
                className="inline-flex items-center justify-center w-5 h-5 rounded text-[#047857] hover:bg-[#10B981]/15 disabled:opacity-50"
              >
                <Check size={10} />
              </button>
              <button
                onClick={dismissShadow}
                disabled={busy}
                data-testid={`step-shadow-dismiss-${index}`}
                title="Discard this pending heal"
                className="inline-flex items-center justify-center w-5 h-5 rounded text-[#B91C1C] hover:bg-[#FEF2F2] disabled:opacity-50"
              >
                <X size={10} />
              </button>
            </>
          )}
        </div>
      )}
      {recoveryHints.map((h, hi) => (
        <div
          key={h.fingerprint}
          className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-[#8B5CF6]/30 bg-[#8B5CF6]/5 text-[10px]"
          data-testid={`step-recovery-hint-${index}-${hi}`}
          title={`AI-learned recovery: ${h.diagnosis || h.category || "unknown state"} — ${h.promoted ? "promoted (replays deterministically without the LLM)" : `promotes after 3 successful recoveries (currently ${h.success_count || 0}/3)`}`}
        >
          <Bot size={9} className="text-[#7C3AED]" />
          <span className="font-medium text-[#6D28D9]">Learned recovery</span>
          {h.category ? (
            <span className="font-mono text-[#475569] max-w-[140px] truncate">{h.category}</span>
          ) : null}
          {h.promoted ? (
            <span className="px-1 rounded bg-[#10B981]/10 text-[#047857] font-semibold">promoted</span>
          ) : (
            <span className="text-[#94A3B8]">{h.success_count || 0}/3</span>
          )}
          {testId && (
            <button
              onClick={() => discardRecoveryHint(h.fingerprint)}
              disabled={busy}
              data-testid={`step-recovery-hint-discard-${index}-${hi}`}
              title="Discard this learned recovery (Probe will re-learn from scratch on the next failure)"
              className="inline-flex items-center justify-center w-5 h-5 rounded text-[#B91C1C] hover:bg-[#FEF2F2] disabled:opacity-50"
            >
              <X size={10} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
